import {
  AttachMoney,
  CheckCircleOutline,
  HourglassTop,
  RequestQuoteOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { PROPOSAL_STATUSES } from "../constants/proposalStatuses";
import { SavedProposal } from "../types/proposal";
import { formatCurrency } from "../utils/currency";

type Props = {
  proposals: SavedProposal[];
};

type SummaryItem = {
  label: string;
  value: string | number;
  helper: string;
  color: "primary" | "info" | "warning" | "success";
  icon: React.ReactNode;
};

const CommercialSummaryCards: React.FC<Props> = ({ proposals }) => {
  const elaboratedIndex = PROPOSAL_STATUSES.indexOf(
    "proposta comercial elaborada",
  );
  const paymentIndex = PROPOSAL_STATUSES.indexOf("pagamento efetuado");
  const totalValue = proposals.reduce(
    (total, proposal) => total + proposal.totals.total,
    0,
  );
  const negotiating = proposals.filter(proposal => {
    const index = PROPOSAL_STATUSES.indexOf(proposal.status);
    return index >= 0 && index <= elaboratedIndex;
  }).length;
  const awaitingPayment = proposals.filter(
    proposal => proposal.status === "link pagamento gerado",
  ).length;
  const paidOrShipping = proposals.filter(
    proposal => PROPOSAL_STATUSES.indexOf(proposal.status) >= paymentIndex,
  ).length;

  const items: SummaryItem[] = [
    {
      label: "Propostas",
      value: proposals.length,
      helper: `${formatCurrency(totalValue)} em oportunidades`,
      color: "primary",
      icon: <RequestQuoteOutlined />,
    },
    {
      label: "Em negociação",
      value: negotiating,
      helper: "Do primeiro contato à elaboração",
      color: "info",
      icon: <HourglassTop />,
    },
    {
      label: "Aguardando pagamento",
      value: awaitingPayment,
      helper: "Com link de pagamento gerado",
      color: "warning",
      icon: <AttachMoney />,
    },
    {
      label: "Pagos ou em envio",
      value: paidOrShipping,
      helper: "Pagamento confirmado em diante",
      color: "success",
      icon: <CheckCircleOutline />,
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {items.map(item => (
        <Grid item xs={12} sm={6} lg={3} key={item.label}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" gap={2}>
                <div>
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>
                    {item.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.helper}
                  </Typography>
                </div>
                <Avatar
                  sx={{
                    bgcolor: `${item.color}.main`,
                    color: `${item.color}.contrastText`,
                  }}
                >
                  {item.icon}
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default CommercialSummaryCards;
